import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

interface LazyPanelBoundaryProps {
  readonly children: ReactNode;
  readonly resetKey: string;
  readonly onReturnToOverview: () => void;
}

interface LazyPanelBoundaryState {
  readonly failed: boolean;
}

/** Keeps a rejected lazy chunk from replacing the whole dashboard shell. */
export class LazyPanelBoundary extends Component<
  LazyPanelBoundaryProps,
  LazyPanelBoundaryState
> {
  state: LazyPanelBoundaryState = { failed: false };

  static getDerivedStateFromError(): LazyPanelBoundaryState {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // The production host can attach its own error telemetry at this boundary.
  }

  componentDidUpdate(previousProps: LazyPanelBoundaryProps) {
    if (this.state.failed && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  private reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.failed) return this.props.children;

    return (
      <section
        role="alert"
        aria-labelledby="panel-load-error-heading"
        className="rounded-xl border border-negative/35 bg-negative/5 p-5 shadow-lg"
        data-testid="lazy-panel-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-negative" />
          <div>
            <h2 id="panel-load-error-heading" className="text-base font-bold text-primary">
              Dashboard panel could not be loaded
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-secondary">
              The application shell is still available. Reload to retry the panel chunk, or return to Overview.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={this.reload}
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-xs font-bold text-inset transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              >
                <RotateCcw aria-hidden="true" className="h-4 w-4" />
                Reload and retry
              </button>
              <button type="button" onClick={this.props.onReturnToOverview} className="secondary-button">
                Return to Overview
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }
}
